import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, Coins, TrendingUp, Gift, ArrowDownLeft, ArrowUpRight, Wallet as WalletIcon, Crown, Sparkles, Lock, Rocket } from "lucide-react";
import { Button } from "../components/ui/button";
import GiftIcon from "../components/GiftIcon";

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-2xl border p-4 ${accent || "border-white/10 bg-white/[0.04]"}`}>
      <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold inline-flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </p>
      <p className="text-2xl font-black tracking-tighter mt-1">{value.toLocaleString()}</p>
    </div>
  );
}

function TxRow({ tx, mine }) {
  const isOut = tx.sender_id === mine;
  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-b-0">
      <div className="shrink-0 w-10 h-10 rounded-full bg-zinc-900 border border-white/10 grid place-items-center">
        <GiftIcon gift={tx.gift_snapshot} size={26} paused />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm">
          <span className="font-bold">{tx.gift_snapshot?.name}</span>
          <span className="text-zinc-400"> {isOut ? "to" : "from"} </span>
          <span className="font-bold">{isOut ? tx.recipient_name : tx.sender_name}</span>
        </p>
        {tx.message && <p className="text-xs text-zinc-500 italic mt-0.5 truncate">"{tx.message}"</p>}
      </div>
      <div className="shrink-0 text-right">
        {isOut ? (
          <p className="text-sm text-pink-300 font-black inline-flex items-center gap-1">
            <ArrowUpRight className="w-3 h-3" /> −{tx.cost_credits}
          </p>
        ) : (
          <p className="text-sm text-cyan-300 font-black inline-flex items-center gap-1">
            <ArrowDownLeft className="w-3 h-3" /> +{tx.recipient_credits}
          </p>
        )}
      </div>
    </div>
  );
}

export default function WalletPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("received");
  const [achievements, setAchievements] = useState(null);
  const [meter, setMeter] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, a, m] = await Promise.all([
        api.get("/wallet/me"),
        api.get("/achievements/me"),
        api.get("/support-meter/me"),
      ]);
      setData(w.data);
      setAchievements(a.data);
      setMeter(m.data);
    } catch (e) {
      toast.error("Couldn't load wallet");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const w = data?.wallet || {};
  const supporters = data?.top_supporters || [];
  const sent = data?.recent_sent || [];
  const received = data?.recent_received || [];
  const mineId = w?.user_id;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24 space-y-6" data-testid="wallet-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"
        data-testid="wallet-back"
      >
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <header className="relative overflow-hidden rounded-3xl border border-pink-400/30 bg-gradient-to-br from-pink-500/15 via-zinc-950 to-cyan-500/15 p-6">
        <p className="text-[10px] uppercase tracking-[0.3em] font-black text-pink-300 inline-flex items-center gap-1">
          <WalletIcon className="w-3 h-3" /> Hidden Gem Wallet
        </p>
        <div className="mt-3 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Credit balance</p>
            <p className="text-5xl font-black tracking-tighter inline-flex items-center gap-2 mt-1">
              <Coins className="w-9 h-9 text-yellow-300" />
              <span data-testid="wallet-balance">{(w.credits || 0).toLocaleString()}</span>
            </p>
            <p className="text-[10px] text-zinc-500 mt-0.5">Spend inside Hidden Gem · not withdrawable</p>
          </div>
          <div className="text-right space-y-1">
            <p className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Earnings available</p>
            <p className="text-3xl font-black tracking-tighter text-cyan-300 inline-flex items-center gap-1.5" data-testid="wallet-earnings-available">
              <TrendingUp className="w-5 h-5" /> {(w.earnings_available_credits || 0).toLocaleString()}
            </p>
            <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Pending</p>
            <p className="text-lg font-black text-zinc-300 tabular-nums" data-testid="wallet-earnings-pending">
              {(w.earnings_pending_credits || 0).toLocaleString()}
            </p>
          </div>
        </div>
        <div className="mt-4 flex gap-2 flex-wrap">
          <Button
            onClick={() => navigate("/credits")}
            className="rounded-full h-10 px-4 bg-gradient-to-r from-pink-500 to-cyan-400 hover:from-pink-400 hover:to-cyan-300 text-white text-xs font-bold"
            data-testid="wallet-buy-credits-btn"
          >
            <Coins className="w-3.5 h-3.5 mr-1.5" /> Buy credits
          </Button>
          <Button
            onClick={() => navigate("/cashout")}
            className="rounded-full h-10 px-4 bg-cyan-500 hover:bg-cyan-400 text-black text-xs font-bold"
            data-testid="wallet-cashout-btn"
            disabled={(w.earnings_available_credits || 0) < 1000}
          >
            <TrendingUp className="w-3.5 h-3.5 mr-1.5" /> Cash out
          </Button>
        </div>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wallet-stats">
        <StatCard label="Lifetime earned" value={w.lifetime_earnings_credits || 0} icon={TrendingUp} />
        <StatCard label="Gifts sent" value={w.gifts_sent_count || 0} icon={ArrowUpRight} />
        <StatCard label="Gifts received" value={w.gifts_received_count || 0} icon={Gift} />
        <StatCard label="Lifetime spent" value={w.lifetime_credits_purchased || 0} icon={Coins} />
      </section>

      {supporters.length > 0 && (
        <section data-testid="wallet-top-supporters">
          <h2 className="text-[10px] uppercase tracking-widest text-pink-300 font-black mb-2 inline-flex items-center gap-1">
            <Crown className="w-3 h-3" /> Top supporters
          </h2>
          <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-1 px-1">
            {supporters.map((s, i) => (
              <button
                key={s.user_id}
                onClick={() => navigate(`/u/${s.user_id}`)}
                className="shrink-0 rounded-2xl border border-white/10 bg-white/[0.04] p-3 w-32 text-center"
                data-testid={`supporter-${s.user_id}`}
              >
                <div className="w-14 h-14 rounded-full bg-pink-500/20 grid place-items-center mx-auto overflow-hidden border border-pink-400/30">
                  {s.avatar_url ? (
                    <img src={fileUrl(s.avatar_url)} alt={s.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-pink-200 font-bold">{(s.name || "?")[0]}</span>
                  )}
                </div>
                <p className="text-xs font-bold mt-2 truncate">{s.name}</p>
                <p className="text-[10px] text-yellow-300 font-black mt-0.5 inline-flex items-center gap-0.5">
                  <Coins className="w-2.5 h-2.5" /> {s.credits_total.toLocaleString()}
                </p>
                {i === 0 && (
                  <span className="text-[9px] uppercase tracking-widest text-pink-300 font-black">#1 fan</span>
                )}
              </button>
            ))}
          </div>
        </section>
      )}

      {meter && (
        <section className="rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-zinc-950 to-pink-500/10 p-5" data-testid="support-meter-card">
          <div className="flex items-center justify-between gap-3 mb-2">
            <p className="text-[10px] uppercase tracking-[0.3em] text-cyan-300 font-black inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Support Meter (7d)
            </p>
            {meter.featured_until && new Date(meter.featured_until) > new Date() && (
              <span className="text-[10px] uppercase tracking-widest font-black bg-yellow-300/20 text-yellow-200 border border-yellow-300/40 rounded-full px-2 py-0.5 inline-flex items-center gap-1" data-testid="featured-badge">
                <Rocket className="w-3 h-3" /> Featured
              </span>
            )}
          </div>
          <div className="flex items-end justify-between gap-3 mb-2">
            <p className="text-2xl font-black tracking-tighter">
              {meter.rolling_7d_credits.toLocaleString()} <span className="text-zinc-500 text-sm font-bold">/ {meter.threshold.toLocaleString()} cr</span>
            </p>
            <p className="text-sm font-black text-cyan-300" data-testid="support-meter-percent">{meter.percent}%</p>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-pink-500 to-cyan-400 transition-all" style={{ width: `${meter.percent}%` }} />
          </div>
          <p className="text-[10px] text-zinc-400 mt-2">Fill the meter to get featured on the homepage for 24 hours.</p>
        </section>
      )}

      {achievements && (
        <section data-testid="achievements-section">
          <h2 className="text-[10px] uppercase tracking-widest text-pink-300 font-black mb-2 inline-flex items-center gap-1">
            <Crown className="w-3 h-3" /> Achievements
            <span className="text-zinc-400 ml-1">{achievements.unlocked.length}/{achievements.catalog.length}</span>
          </h2>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {achievements.catalog.map((a) => (
              <div
                key={a.key}
                className={`rounded-2xl p-3 border text-center transition-colors ${
                  a.unlocked
                    ? "border-yellow-300/40 bg-yellow-300/[0.06] shadow-[0_0_18px_-6px_rgba(250,204,21,.55)]"
                    : "border-white/10 bg-white/[0.02] opacity-60"
                }`}
                title={a.description}
                data-testid={`achievement-${a.key}`}
              >
                <div className="text-3xl">{a.unlocked ? a.badge_emoji : <Lock className="w-5 h-5 mx-auto text-zinc-600" />}</div>
                <p className="text-[10px] font-black mt-1 truncate">{a.title}</p>
                <p className="text-[9px] text-zinc-500 mt-0.5">{a.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <div className="flex items-center gap-2 mb-3">
          <button
            onClick={() => setTab("received")}
            className={`rounded-full h-9 px-4 text-xs font-bold uppercase tracking-widest border ${
              tab === "received"
                ? "bg-cyan-500/15 text-cyan-200 border-cyan-400/40"
                : "bg-white/5 text-zinc-300 border-white/10"
            }`}
            data-testid="wallet-tab-received"
          >
            Received ({received.length})
          </button>
          <button
            onClick={() => setTab("sent")}
            className={`rounded-full h-9 px-4 text-xs font-bold uppercase tracking-widest border ${
              tab === "sent"
                ? "bg-pink-500/15 text-pink-200 border-pink-400/40"
                : "bg-white/5 text-zinc-300 border-white/10"
            }`}
            data-testid="wallet-tab-sent"
          >
            Sent ({sent.length})
          </button>
        </div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3" data-testid="wallet-tx-list">
          {loading ? (
            <p className="text-center text-zinc-500 py-8">Loading…</p>
          ) : (tab === "received" ? received : sent).length === 0 ? (
            <div className="py-10 text-center text-zinc-500">
              <Gift className="w-7 h-7 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No {tab} gifts yet.</p>
            </div>
          ) : (
            (tab === "received" ? received : sent).map((tx) => (
              <TxRow key={tx.tx_id} tx={tx} mine={mineId} />
            ))
          )}
        </div>
      </section>
    </div>
  );
}
